import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Users, Eye, Globe, MapPin, Clock, Activity } from 'lucide-react';
import { useAnalytics } from '../../hooks/useAnalytics';

interface RealtimeAnalyticsProps {
  projectId?: string;
}

interface DeviceBreakdown {
  device: string;
  users: number;
  percentage: number;
  color: string;
}

export function RealtimeAnalytics({ projectId }: RealtimeAnalyticsProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const analytics = useAnalytics(projectId);
  const { data: realtimeData, isLoading, error } = analytics.useRealtime();
  
  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Use real data if available, otherwise fallback to mock data
  const activeUsers = realtimeData?.activeUsers || 42;
  const pageviews = realtimeData?.pageviews || 234;

  const realtimeMetrics = [
    {
      title: 'Active Users',
      value: activeUsers.toString(),
      icon: Users,
      color: 'green',
      pulse: true
    },
    {
      title: 'Page Views (Last 30 min)',
      value: pageviews.toString(),
      icon: Eye,
      color: 'blue',
      pulse: false
    },
    {
      title: 'Active Pages',
      value: realtimeData?.topPages?.length?.toString() || '12',
      icon: Globe,
      color: 'purple',
      pulse: false
    },
    {
      title: 'Avg. Load Time',
      value: '1.2s', // TODO: Add load time to realtime API
      icon: Clock,
      color: 'orange',
      pulse: false
    }
  ];

  // Process active pages from real data
  const activePages = realtimeData?.topPages?.slice(0, 5)?.map((page: { page: string; users: number; percentage: number }) => ({
    page: page.page,
    users: page.users,
    percentage: page.percentage
  })) || [
    { page: '/home', users: 18, percentage: 42.9 },
    { page: '/products', users: 12, percentage: 28.6 },
    { page: '/about', users: 6, percentage: 14.3 },
    { page: '/contact', users: 4, percentage: 9.5 },
    { page: '/blog', users: 2, percentage: 4.8 }
  ];

  // Device breakdown from real GA4 data
  const deviceBreakdown = realtimeData?.deviceBreakdown || [
    { device: 'Mobile', users: Math.floor((realtimeData?.activeUsers || 42) * 0.6), percentage: 60, color: 'bg-green-500' },
    { device: 'Desktop', users: Math.floor((realtimeData?.activeUsers || 42) * 0.35), percentage: 35, color: 'bg-blue-500' },
    { device: 'Tablet', users: Math.floor((realtimeData?.activeUsers || 42) * 0.05), percentage: 5, color: 'bg-purple-500' }
  ];

  // Process top countries from real data
  const topCountries = realtimeData?.topCountries?.slice(0, 7)?.map((country: { country: string; users: number }) => ({
    country: country.country,
    flag: getCountryFlag(country.country),
    users: country.users
  })) || [
    { country: 'United States', flag: '🇺🇸', users: 18 },
    { country: 'United Kingdom', flag: '🇬🇧', users: 8 },
    { country: 'Canada', flag: '🇨🇦', users: 6 },
    { country: 'Germany', flag: '🇩🇪', users: 4 },
    { country: 'Australia', flag: '🇦🇺', users: 3 },
    { country: 'France', flag: '🇫🇷', users: 2 },
    { country: 'Japan', flag: '🇯🇵', users: 1 }
  ];

  // Process traffic sources from real data
  const trafficSources = realtimeData?.topSources?.slice(0, 5)?.map((source: { source: string; users: number }, index: number) => ({
    source: source.source,
    users: source.users,
    color: getColorForSource(source.source, index)
  })) || [
    { source: 'Direct', users: 16, color: 'bg-blue-500' },
    { source: 'Organic Search', users: 14, color: 'bg-green-500' },
    { source: 'Social Media', users: 8, color: 'bg-purple-500' },
    { source: 'Referral', users: 3, color: 'bg-orange-500' },
    { source: 'Email', users: 1, color: 'bg-pink-500' }
  ];

  function getCountryFlag(country: string): string {
    const flagMap: Record<string, string> = {
      'United States': '🇺🇸',
      'United Kingdom': '🇬🇧',
      'Canada': '🇨🇦',
      'Germany': '🇩🇪',
      'Australia': '🇦🇺',
      'France': '🇫🇷',
      'Japan': '🇯🇵',
      'Italy': '🇮🇹',
      'Spain': '🇪🇸',
      'Netherlands': '🇳🇱',
      'Brazil': '🇧🇷',
      'India': '🇮🇳',
      'China': '🇨🇳'
    };
    return flagMap[country] || '🌍';
  }

  function getColorForSource(source: string, index: number): string {
    const sourceColors: Record<string, string> = {
      'Organic Search': 'bg-blue-500',
      'Direct': 'bg-green-500',
      'Social Media': 'bg-purple-500',
      'Social': 'bg-purple-500',
      'Referral': 'bg-orange-500',
      'Email': 'bg-pink-500',
      'Paid Search': 'bg-red-500',
      'Display': 'bg-indigo-500'
    };
    
    if (sourceColors[source]) return sourceColors[source];
    
    const fallbackColors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 
      'bg-pink-500', 'bg-red-500', 'bg-indigo-500', 'bg-cyan-500'
    ];
    return fallbackColors[index % fallbackColors.length];
  }



  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400">Error loading real-time data</p>
            <p className="text-sm text-red-500 dark:text-red-400 mt-1">{error.message}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            Real-time Analytics
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">Live data from your website</p>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Last updated: {currentTime.toLocaleTimeString()} {projectId && `• Project: ${projectId}`}
        </div>
      </div>

      {/* Data Source Indicator */}
      {realtimeData?.dataSource && (
        <Card className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <Activity className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {realtimeData.dataSource === 'ga4_realtime_real' ? 'Live GA4 Real-time Data' : 
                 realtimeData.dataSource === 'ga4_realtime_fallback' ? 'GA4 Connected (Fallback Data)' :
                 'Sample Data'}
              </h3>
              <p className="text-sm text-gray-600">
                {realtimeData.dataSource === 'ga4_realtime_real' 
                  ? 'Live real-time data from Google Analytics 4 (updated every 30 seconds)'
                  : realtimeData.dataSource === 'ga4_realtime_fallback' 
                    ? 'GA4 connected but using sample data due to API limitations'
                    : 'Sample data for demonstration'
                }
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Real-time Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {realtimeMetrics.map((metric, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{metric.title}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{metric.value}</p>
                {metric.pulse && (
                  <div className="flex items-center mt-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    <span className="text-sm text-green-600">Live</span>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {realtimeData?.dataSource === 'ga4_realtime_real' ? 'Live GA4 data' : 
                   realtimeData?.dataSource ? 'Connected' : 'Sample data'}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                metric.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
                metric.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                metric.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
                'bg-orange-100 dark:bg-orange-900/30'
              }`}>
                <metric.icon className={`w-6 h-6 ${
                  metric.color === 'green' ? 'text-green-600 dark:text-green-400' :
                  metric.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                  metric.color === 'purple' ? 'text-purple-600 dark:text-purple-400' :
                  'text-orange-600 dark:text-orange-400'
                }`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Pages */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Active Pages Right Now</h3>
          <div className="space-y-4">
            {activePages.map((page: { page: string; users: number; percentage: number }, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">{page.page}</p>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${page.percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="ml-4 text-right">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    <span className="font-semibold text-gray-900 dark:text-white">{page.users}</span>
                  </div>
                  <p className="text-sm text-gray-500">{page.percentage}%</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Device Breakdown */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Active Users by Device</h3>
          <div className="space-y-4">
                         {deviceBreakdown.map((device: DeviceBreakdown, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full ${device.color} mr-3`}></div>
                  <span className="font-medium text-gray-900 dark:text-white">{device.device}</span>
                </div>
                <div className="flex items-center space-x-3">
                                      <div className="flex-1 w-20">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${device.color.replace('bg-', 'bg-')}`}
                        style={{ width: `${device.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="text-right min-w-[60px]">
                    <div className="flex items-center justify-end">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                      <span className="font-semibold text-gray-900 dark:text-white">{device.users}</span>
                    </div>
                    <p className="text-sm text-gray-500">{device.percentage}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              {realtimeData?.dataSource === 'ga4_realtime_real' ? 'Live device breakdown from GA4' : 'Sample device data'}
            </p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Countries */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Active Users by Country</h3>
          <div className="space-y-3">
            {topCountries.map((country: { country: string; flag: string; users: number }, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-xl mr-3">{country.flag}</span>
                  <span className="font-medium text-gray-900 dark:text-white">{country.country}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
                  <span className="font-semibold text-gray-900 dark:text-white">{country.users}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Traffic Sources */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Active Traffic Sources</h3>
          <div className="space-y-3">
            {trafficSources.map((source: { source: string; users: number; color: string }, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full ${source.color} mr-3`}></div>
                  <span className="font-medium text-gray-900 dark:text-white">{source.source}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                  <span className="font-semibold text-gray-900 dark:text-white">{source.users}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
} 